import { Typography } from "@material-ui/core";
import produce from "immer";
import { cloneDeep, isArray, mergeWith } from "lodash";
import moment from "moment";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DeepPartial } from "react-hook-form";
import styled from "styled-components";
import { ReactComponent as LogoSVG } from "../../../../../assets/logo-long.svg";
import { CenteredFlex, columnFlex } from "../../../../../components/layout";
import { WrapWithStepperContext } from "../../../../../components/stepper/context";
import {
  AddressAttachmentType,
  IdentityAttachmentType,
} from "../../../../../store/common/models";
import { VerificationDTO } from "../../../../../store/verification/models/dto/due-diligence";
import { DueDiligenceFileCategory } from "../../../../../store/verification/models/internal";
import { VerificationService } from "../../../../../store/verification/service";
import { HeaderContainer, LaunchLogo } from "../../../common/header";
import { ConfigContextProvider } from "../common/config-context";
import ExpiredForm from "../common/expired";
import { FileAttachmentContext } from "../common/pages/kyc-details/file-attachment-context";
import { IdTypeContextProvider } from "../common/pages/kyc-details/id-type-context";
import { AddressTypeContextProvider } from "../common/pages/kyc-details/proof-of-address-type-context";
import AddressStep from "../common/pages/kyc-details/steps/address";
import IdentityStep from "../common/pages/kyc-details/steps/identity";
import ProofOfAddressStep from "../common/pages/kyc-details/steps/proof-of-address";
import { FormVerticalLinearStepper } from "../common/steppers";
import AlreadySubmitted from "./already-submitted";
import SelfieStep from "./selfie";
import UserDetails from "./user-details";

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Participant.T;

const Wrapper = styled.div`
  ${columnFlex};
  flex-grow: 1;
`;

const PoweredBy = styled.a`
  ${CenteredFlex};
  justify-content: flex-end;
  font-size: 0.8em;
  margin-top: 1em;
`;

const StyledLogo = styled(LogoSVG)`
  width: 8em;
  margin-left: 1.5em;
`;

export const ParticipantDueDiligenceForm: FunctionComponent<{
  item: VerificationDTO.Responses.DueDiligence.Config.Get.T;
  id: string;
  code: string;
}> = ({ item, id, code }) => {
  const {
    header: { css },
    expiresAt,
  } = item;

  const hasExpired = useMemo(() => expiresAt?.isBefore(moment()), [expiresAt]);
  const [finaliseData, setFinaliseData] = useState<DeepPartial<FormType>>({});

  useEffect(() => {
    VerificationService.getParticipantDetails(code).then((data) => {
      setFinaliseData({
        userDetails: data,
      });
    });
  }, [code]);

  const setData = useCallback(
    (d: DeepPartial<FormType>) =>
      setFinaliseData((curr) => {
        const result = {};
        mergeWith(result, curr, d, (_: any, srcValue: any) => {
          // Make sure that if we have an array (e.g. social media links), any subtraction is accounted for
          if (isArray(srcValue)) {
            return srcValue;
          }
        });
        return result;
      }),
    [setFinaliseData]
  );

  const [submitted, setSubmitted] = useState<boolean>();

  useEffect(() => {
    VerificationService.validateParticipantSubmission(item.dueDiligenceId, code)
      .then(() => {
        setSubmitted(false);
      })
      .catch(() => setSubmitted(true));
  }, []);

  const steps = useMemo(
    () => [
      {
        title: "User Details",
        content: UserDetails,
      },
      {
        title: "Address",
        content: AddressStep,
      },
      {
        title: "ID Details",
        content: IdentityStep,
      },
      {
        title: "Proof of Address",
        content: ProofOfAddressStep,
      },
      {
        title: "Face",
        content: SelfieStep,
      },
    ],
    []
  );

  const [idType, setIdType] = useState<IdentityAttachmentType>();
  const [addressType, setAddressType] = useState<AddressAttachmentType>();
  const [fileAttachments, setFileAttachments] = useState<
    Partial<
      Record<
        typeof DueDiligenceFileCategory.T,
        VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T
      >
    >
  >({});

  const stepsProps = useMemo(
    () => ({
      setData,
      data: finaliseData,
      code,
    }),
    [setData, finaliseData, code]
  );

  if (submitted === undefined) return null; // still loading
  return (
    <ConfigContextProvider item={item} code={code}>
      <IdTypeContextProvider idType={idType} setIdType={setIdType}>
        <AddressTypeContextProvider
          addressType={addressType}
          setAddressType={setAddressType}
        >
          <FileAttachmentContext.Provider
            value={{
              setFile: (type, file) =>
                setFileAttachments(
                  produce((x) => {
                    x[type] = file;
                  })
                ),
              files: fileAttachments,
              emailAddress: finaliseData.userDetails?.emailAddress,
            }}
          >
            <Wrapper>
              <HeaderContainer
                {...css?.backgroundColor}
                colour={css?.backgroundColor.end}
              >
                <LaunchLogo
                  src={VerificationService.getDueDiligenceHeaderPath(id)}
                />
              </HeaderContainer>
              {hasExpired ? (
                <ExpiredForm />
              ) : submitted ? (
                <AlreadySubmitted />
              ) : (
                <WrapWithStepperContext>
                  <div
                    style={{
                      width: "100%",
                      background: "#ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <div style={{ padding: "2em" }}>
                      <Typography>
                        Please provide further information and documents as part
                        of the due diligence process.
                      </Typography>
                    </div>
                    <FormVerticalLinearStepper
                      stepsProps={stepsProps}
                      steps={steps}
                    />
                  </div>
                </WrapWithStepperContext>
              )}
            </Wrapper>
            <PoweredBy
              href="http://www.sekuritance.com"
              rel="noreferrer"
              target="_blank"
            >
              <div>Powered by</div>
              <StyledLogo />
            </PoweredBy>
          </FileAttachmentContext.Provider>
        </AddressTypeContextProvider>
      </IdTypeContextProvider>
    </ConfigContextProvider>
  );
};
